import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AlertTriangle, ChevronRight, Edit, Plus, Sparkles, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CitationChip } from '@/components/ui/citation-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';
import { Spinner } from '@/components/ui/spinner';
import { ContractHeader } from '@/components/contract/contract-header';
import {
  CheckpointDrawer,
  CheckpointStateChip,
  sourceCitationChip,
  type CheckpointDrawerMode,
} from '@/components/contract/checkpoint-drawer';
import {
  NEEDS_ACTION,
  checkpointLabel,
  checkpointState,
  checkpointTileCounts,
  formatDay,
  nextToScore,
  relativeDays,
} from '@/components/contract/checkpoint-status';
import { CheckpointScoringModal } from '@/components/modals/checkpoint-scoring-modal';
import { useCheckpointsListQuery, type Checkpoint } from '@/lib/api/checkpoints';
import { useProposalQueueQuery } from '@/lib/api/review-proposals';
import { cn } from '@/lib/utils/cn';
import { pluralize } from '@/lib/contract-labels';

/**
 * /contract/checkpoints — IV&V checkpoint schedule (E06-F04, scoring E06-F05).
 *
 * Checkpoints come from the SOW: base formation extracts them, the
 * consultant approves them in the AI review queue, and they land here in the
 * SOW's own order. The tab answers, per checkpoint: which one, when, what it
 * covers, how the last round went, and where it came from. Clicking a row
 * opens the checkpoint drawer (detail, edit, scoring history); scoring opens
 * the existing scoring modal.
 */
export const Route = createFileRoute('/engagements/$engagementId/contract/checkpoints')({
  component: ContractCheckpointsTab,
});

type Filter = 'all' | 'action' | 'archived';

function ContractCheckpointsTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/contract/checkpoints' });
  const [filter, setFilter] = useState<Filter>('all');
  const [drawer, setDrawer] = useState<CheckpointDrawerMode | null>(null);
  const [scoring, setScoring] = useState<Checkpoint | null>(null);

  const active = useCheckpointsListQuery(engagementId, { status: 'active' });
  const archived = useCheckpointsListQuery(engagementId, { status: 'archived' });
  const pendingProposals = useProposalQueueQuery(engagementId, {
    decision: 'pending',
    proposal_type: 'checkpoint',
    limit: 200,
  });

  const activeItems = useMemo(() => active.data?.items ?? [], [active.data]);
  const archivedItems = useMemo(() => archived.data?.items ?? [], [archived.data]);
  const pendingCount = pendingProposals.data?.items.length ?? 0;

  const states = useMemo(
    () => new Map(activeItems.map((c) => [c.checkpoint_id, checkpointState(c)])),
    [activeItems],
  );
  const needsAction = activeItems.filter((c) => NEEDS_ACTION.has(states.get(c.checkpoint_id)!.key));
  const needsCriteria = activeItems.filter(
    (c) => states.get(c.checkpoint_id)!.key === 'needs-criteria',
  );
  const tiles = checkpointTileCounts(activeItems);
  const next = nextToScore(activeItems);
  const nextSequence =
    [...activeItems, ...archivedItems].reduce((max, c) => Math.max(max, c.sequence), 0) + 1;

  const rows =
    filter === 'archived' ? archivedItems : filter === 'action' ? needsAction : activeItems;
  const openRow = (c: Checkpoint) => setDrawer({ mode: 'view', checkpointId: c.checkpoint_id });

  const meta =
    activeItems.length === 0
      ? pendingCount > 0
        ? `${pendingCount} checkpoint ${pendingCount === 1 ? 'proposal is' : 'proposals are'} waiting for review`
        : 'No checkpoints yet'
      : [
          `${activeItems.length} ${activeItems.length === 1 ? 'checkpoint' : 'checkpoints'}`,
          `${tiles.met} met`,
          `${tiles.conditional} conditional`,
          `${tiles.missed} missed`,
          `${tiles.overdue} overdue`,
        ].join(' · ');

  return (
    <div className="space-y-5">
      <ContractHeader
        engagementId={engagementId}
        active="checkpoints"
        meta={<span>{meta}</span>}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDrawer({ mode: 'create' })}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add checkpoint
            </Button>
            {next ? (
              <Button
                type="button"
                size="sm"
                className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
                onClick={() => openRow(next)}
              >
                <Edit className="h-4 w-4" aria-hidden="true" />
                Score {checkpointLabel(next) ?? 'next checkpoint'}
              </Button>
            ) : null}
          </>
        }
      />

      {active.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading checkpoints…
        </div>
      ) : active.error ? (
        <ErrorMessage error={active.error} />
      ) : activeItems.length === 0 && archivedItems.length === 0 ? (
        <NoCheckpoints
          engagementId={engagementId}
          pendingCount={pendingCount}
          onAdd={() => setDrawer({ mode: 'create' })}
        />
      ) : (
        <>
          {pendingCount > 0 ? (
            <div className="flex items-center justify-between gap-4 rounded-card border border-ai-surface-border bg-ai-surface px-4 py-3 text-body-sm text-ai-ink">
              <span className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                {pendingCount} more checkpoint{' '}
                {pendingCount === 1 ? 'proposal is' : 'proposals are'} waiting in AI review. They
                appear here once approved.
              </span>
              <Button asChild variant="outline" size="sm">
                <Link to="/engagements/$engagementId/proposals" params={{ engagementId }}>
                  Review
                </Link>
              </Button>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
            <NextCheckpointTile checkpoint={next} onOpen={openRow} />
            <CountTile
              label="Met"
              value={tiles.met}
              suffix={`of ${activeItems.length}`}
              tone="healthy"
              source="Latest scoring round"
            />
            <CountTile
              label="Conditional"
              value={tiles.conditional}
              tone={tiles.conditional > 0 ? 'at-risk' : 'neutral'}
              source="Latest scoring round"
            />
            {/* C2-23: a failed (red) round counts here; it is neither met nor overdue. */}
            <CountTile
              label="Missed"
              value={tiles.missed}
              tone={tiles.missed > 0 ? 'critical' : 'neutral'}
              source="Latest scoring round"
            />
            <CountTile
              label="Overdue"
              value={tiles.overdue}
              tone={tiles.overdue > 0 ? 'critical' : 'neutral'}
              source="Due date, not yet scored"
            />
          </div>

          {needsCriteria.length > 0 ? (
            <div className="flex items-center justify-between gap-4 rounded-card border border-ink-100 border-l-4 border-l-posture-at-risk bg-paper-0 px-4 py-3 text-body-sm text-ink-700">
              <span className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-posture-at-risk" aria-hidden="true" />
                <span>
                  <strong className="font-semibold text-ink-900">
                    {needsCriteria.length === 1
                      ? (checkpointLabel(needsCriteria[0]!) ?? needsCriteria[0]!.name)
                      : `${needsCriteria.length} checkpoints`}
                  </strong>{' '}
                  {needsCriteria.length === 1 ? 'has' : 'have'} no assessment criteria, so{' '}
                  {needsCriteria.length === 1 ? 'it' : 'they'} can't be scored yet.
                </span>
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setDrawer({ mode: 'edit', checkpointId: needsCriteria[0]!.checkpoint_id })
                }
              >
                Add criteria
              </Button>
            </div>
          ) : null}

          <Card>
            <CardContent className="p-0">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-100 px-5 py-3">
                <div>
                  <h2 className="text-h3 font-semibold text-ink-900">Checkpoint schedule</h2>
                  <p className="text-meta text-ink-500">
                    In SOW order. Click a checkpoint for its criteria, SOW source and scoring
                    history.
                  </p>
                </div>
                <FilterChips
                  value={filter}
                  onChange={setFilter}
                  counts={{
                    all: activeItems.length,
                    action: needsAction.length,
                    archived: archivedItems.length,
                  }}
                />
              </div>
              {rows.length === 0 ? (
                <p className="px-5 py-6 text-body-sm text-ink-500">
                  {filter === 'archived'
                    ? 'No archived checkpoints.'
                    : 'Nothing needs action right now.'}
                </p>
              ) : (
                <CheckpointTable rows={rows} onOpen={openRow} />
              )}
            </CardContent>
          </Card>
        </>
      )}

      <CheckpointDrawer
        engagementId={engagementId}
        checkpoints={[...activeItems, ...archivedItems]}
        nextSequence={nextSequence}
        state={drawer}
        onStateChange={setDrawer}
        onScore={(c) => {
          setDrawer(null);
          setScoring(c);
        }}
      />
      {scoring ? (
        <CheckpointScoringModal
          checkpoint={scoring}
          open
          onOpenChange={(o) => !o && setScoring(null)}
        />
      ) : null}
    </div>
  );
}

// ===========================================================================
// Table
// ===========================================================================

function CheckpointTable({
  rows,
  onOpen,
}: {
  rows: ReadonlyArray<Checkpoint>;
  onOpen: (c: Checkpoint) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-body-sm">
        <thead>
          <tr className="border-b border-ink-100 bg-paper-50 text-left">
            <Th className="w-[72px]">#</Th>
            <Th>Checkpoint</Th>
            <Th className="w-[170px]">Due</Th>
            <Th className="w-[110px]">Assesses</Th>
            <Th className="w-[200px]">Latest result</Th>
            <Th className="w-[200px]">Source</Th>
            <Th className="w-[36px]">
              <span className="sr-only">Open</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <CheckpointRow key={c.checkpoint_id} checkpoint={c} onOpen={onOpen} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CheckpointRow({
  checkpoint: c,
  onOpen,
}: {
  checkpoint: Checkpoint;
  onOpen: (c: Checkpoint) => void;
}) {
  const state = checkpointState(c);
  const cite = sourceCitationChip(c);
  return (
    <tr
      tabIndex={0}
      aria-label={`Open ${checkpointLabel(c) ?? ''} ${c.name}`}
      onClick={() => onOpen(c)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(c);
        }
      }}
      className="cursor-pointer border-b border-ink-100 last:border-b-0 hover:bg-paper-50 focus:bg-paper-50 focus:outline-none"
    >
      <Td className="whitespace-nowrap font-mono text-mono-sm text-ink-500">
        {checkpointLabel(c) ?? '—'}
      </Td>
      <Td>
        <div className="font-semibold text-ink-900">{c.name}</div>
        {c.purpose ? (
          <div className="max-w-[520px] truncate text-meta text-ink-500">{c.purpose}</div>
        ) : null}
      </Td>
      <Td className="whitespace-nowrap">
        {c.due_date ? (
          <>
            <div className="font-mono text-mono-sm text-ink-900">{formatDay(c.due_date)}</div>
            <div className="max-w-[160px] truncate text-meta text-ink-500">
              {c.milestone?.name ?? relativeDays(c.due_date)}
            </div>
          </>
        ) : (
          <>
            <div className="text-meta text-ink-500">No date</div>
            {c.sow_timing ? (
              <div className="max-w-[160px] truncate text-meta text-ink-500">
                SOW: {c.sow_timing}
              </div>
            ) : null}
          </>
        )}
      </Td>
      <Td className="whitespace-nowrap text-ink-700">
        {c.criteria.length > 0 ? pluralize(c.criteria.length, 'criterion', 'criteria') : '—'}
      </Td>
      <Td className="whitespace-nowrap">
        <CheckpointStateChip state={state} />
        <div className="mt-1 text-meta text-ink-500">{state.detail}</div>
      </Td>
      <Td>
        {cite ? (
          // No handler: the click bubbles to the row, and the drawer opens the passage.
          <CitationChip cite={cite} />
        ) : (
          <span className="text-meta text-ink-500">
            {c.source === 'ai_proposed_confirmed' ? 'AI · no citation' : 'Added manually'}
          </span>
        )}
      </Td>
      <Td>
        <ChevronRight className="h-4 w-4 text-ink-300" aria-hidden="true" />
      </Td>
    </tr>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-3 align-middle', className)}>{children}</td>;
}

// ===========================================================================
// Tiles, filters, empty state
// ===========================================================================

function NextCheckpointTile({
  checkpoint,
  onOpen,
}: {
  checkpoint: Checkpoint | null;
  onOpen: (c: Checkpoint) => void;
}) {
  return (
    <Card className="border-l-[3px] border-l-accent-amber-600">
      <CardContent className="flex h-full items-center gap-4 p-4">
        {checkpoint ? (
          <>
            {checkpointLabel(checkpoint) ? (
              <div className="font-mono text-h2 font-semibold text-ink-900">
                {checkpointLabel(checkpoint)}
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="text-meta uppercase tracking-wide text-ink-500">Next to score</div>
              <div className="truncate font-semibold text-ink-900">{checkpoint.name}</div>
              <div className="text-meta text-ink-500">
                {formatDay(checkpoint.due_date!)} · {relativeDays(checkpoint.due_date!)}
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => onOpen(checkpoint)}>
              Open
            </Button>
          </>
        ) : (
          <div>
            <div className="text-meta uppercase tracking-wide text-ink-500">Next to score</div>
            <div className="mt-1 text-body-sm text-ink-500">
              Nothing scheduled. Give checkpoints a due date to see what is next.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CountTile({
  label,
  value,
  suffix,
  tone,
  source,
}: {
  label: string;
  value: number;
  suffix?: string;
  tone: 'neutral' | 'healthy' | 'at-risk' | 'critical';
  source: string;
}) {
  const valueClass =
    tone === 'critical'
      ? 'text-posture-critical'
      : tone === 'at-risk'
        ? 'text-posture-at-risk'
        : tone === 'healthy'
          ? 'text-posture-healthy'
          : 'text-ink-900';
  return (
    <Card>
      <CardContent className="space-y-1.5 p-4">
        <div className="text-meta uppercase tracking-wide text-ink-500">{label}</div>
        <div className={cn('text-h2 font-semibold tabular-nums', valueClass)}>
          {value}
          {suffix ? (
            <span className="ml-1 text-body-sm font-medium text-ink-500">{suffix}</span>
          ) : null}
        </div>
        <KpiSourcePill source={source} />
      </CardContent>
    </Card>
  );
}

function FilterChips({
  value,
  onChange,
  counts,
}: {
  value: Filter;
  onChange: (f: Filter) => void;
  counts: Record<Filter, number>;
}) {
  const options: Array<[Filter, string]> = [
    ['all', 'All'],
    ['action', 'Needs action'],
    ['archived', 'Archived'],
  ];
  return (
    <div role="tablist" aria-label="Filter checkpoints" className="flex gap-2">
      {options.map(([key, text]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={value === key}
          onClick={() => onChange(key)}
          className={cn(
            'inline-flex h-[26px] items-center gap-1.5 rounded-full border px-3 text-meta font-medium',
            value === key
              ? 'border-ink-900 bg-ink-900 text-paper-0'
              : 'border-ink-100 bg-paper-100 text-ink-700 hover:border-ink-200',
          )}
        >
          {text}
          <span className="font-mono">{counts[key]}</span>
        </button>
      ))}
    </div>
  );
}

function NoCheckpoints({
  engagementId,
  pendingCount,
  onAdd,
}: {
  engagementId: string;
  pendingCount: number;
  onAdd: () => void;
}) {
  const waiting = pendingCount > 0;
  return (
    <Card>
      <CardContent className="flex gap-4 p-6">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-card border',
            waiting
              ? 'border-ai-surface-border bg-ai-surface text-ai-ink'
              : 'border-ink-100 bg-paper-100 text-ink-500',
          )}
        >
          {waiting ? (
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Upload className="h-4 w-4" aria-hidden="true" />
          )}
        </div>
        <div className="space-y-3">
          <div>
            <h2 className="text-body font-semibold text-ink-900">
              {waiting
                ? `${pendingCount} checkpoint ${pendingCount === 1 ? 'proposal is' : 'proposals are'} waiting for review`
                : 'Checkpoints come from the SOW'}
            </h2>
            <p className="mt-0.5 max-w-2xl text-body-sm text-ink-700">
              {waiting
                ? 'The AI read the SOW and proposed these checkpoints. Nothing is added here until you approve it. They keep the SOW’s own numbering, whatever order you approve them in.'
                : 'IV&V checkpoints are the points where Avero formally assesses the vendor. The SOW usually lists them with their timing and what each one covers. Upload the SOW and the AI proposes them for your review.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {waiting ? (
              <Button
                asChild
                size="sm"
                className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
              >
                <Link to="/engagements/$engagementId/proposals" params={{ engagementId }}>
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  Review {pendingCount} {pendingCount === 1 ? 'proposal' : 'proposals'}
                </Link>
              </Button>
            ) : (
              <Button
                asChild
                size="sm"
                className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
              >
                <Link to="/engagements/$engagementId/documents" params={{ engagementId }}>
                  <Upload className="h-4 w-4" aria-hidden="true" />
                  Upload the SOW
                </Link>
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={onAdd}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add one manually
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
